import { format, isValid } from "date-fns/fp";
import { always, chain, filter, map, pipe, splitEvery } from "ramda";
import {
  useAuth,
  useMyMutation,
  useMyQuery,
} from "../Authorization/AuthProvider";
import { padLeft } from "../Availability/api";
import {
  dayRangesName,
  enabledName,
} from "./AvailabilitySettings/AvailabilitySettings";
import { INTEGRATION_TYPE } from "./AvailabilitySettings/IntegrationStatusButton";
import { AVAILABILITY_TYPE, DAY_NAMES } from "./constants";

export const useRecurringAvailabilityQuery = ({ enabled = true } = {}) => {
  const query = useMyQuery({
    enabled,
    queryKey: ["coach-availability", AVAILABILITY_TYPE.RECURRING],
    fetchDef: {
      url: `/api/latest/coach-availability/${AVAILABILITY_TYPE.RECURRING}`,
    },
    // When empty:
    // Request URL: http://localhost:3000/api/latest/coach-availability/ALL
    // Request Method: GET
    // Status Code: 400 Bad Request
    // RECURRING  initially "{}"
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return query;
};

// dayRanges = [start,end,start,end,...]
const throwOnInvalidDayRanges = ({ dayRanges }) => {
  if (dayRanges.some((date) => !isValid(date))) {
    console.error({
      dayRanges,
      firstInvalidIndex: dayRanges.findIndex((date) => !isValid(date)),
    });
    throw new Error(`Invalid dayRanges, all dates must be valid`);
  }
  if (dayRanges.length % 2 !== 0) {
    throw new Error(
      `Invalid dayRanges, length must be factor of 2 [start,end,start,end,...]`,
    );
  }
};

export const useRecurringAvailabilityMutation = () => {
  return useMyMutation({
    debug: true,
    fetchDef: {
      method: "POST",
      url: `/api/latest/coach-availability/${AVAILABILITY_TYPE.RECURRING}`,
      from: (values) =>
        pipe(
          always(DAY_NAMES),
          map((day) => ({
            day,
            enabled: values[enabledName(day)],
            dayRanges: values[dayRangesName(day)],
          })),
          filter(({ enabled } = {}) => enabled),
          chain(({ dayRanges, day }) => {
            throwOnInvalidDayRanges({ dayRanges });
            return dayRanges.map((date) => {
              const hours = format("kk", date).replace("24", "00");
              const minutes = format("mm", date);
              return {
                day,
                time: `${padLeft("0", hours)}:${padLeft("0", minutes)}:00`, // "09:00:00"
              };
            });
          }),
          splitEvery(2),
          map(([from, to]) => ({ from, to })),
        )(),
    },
    invalidate: [
      { queryKey: ["coach-availability"] },
      { queryKey: ["coaches"] },
    ],
  });
};

export const useIntegrationInfoQuery = ({ integrationType }) => {
  const url = {
    [INTEGRATION_TYPE.GOOGLE]: "/api/latest/google-info",
    [INTEGRATION_TYPE.CALENDLY]: "/api/latest/calendly-info",
  }[integrationType];
  if (!url) throw new Error("Url not found");

  return useMyQuery({
    queryKey: ["coach-availability", "integration-info", integrationType],
    fetchDef: { url },
    retry: 1,
  });
};

const useAvailabilitySettingsQuery = () => {
  return useMyQuery({
    queryKey: ["coach-availability", "settings"],
    fetchDef: { url: `/api/latest/coach-availability/recurring/settings` },
    retry: 1,
  });
};

export const useAvailabilitySettingsMutation = () => {
  const {
    userQuery: {
      data: { username },
    },
  } = useAuth();

  return useMyMutation({
    fetchDef: {
      method: "PATCH",
      url: `/api/latest/coach-availability/recurring/settings`,
    },
    invalidate: [
      { queryKey: ["coaches", username, "availability"], exact: false },
      { queryKey: ["coach-availability"], exact: false },
    ],
  });
};

export const useDisconnectMutation = ({ integrationType }) => {
  return useMyMutation({
    fetchDef: {
      method: "DELETE",
      url: {
        [INTEGRATION_TYPE.CALENDLY]: `/api/latest/calendly-disconnect`,
        [INTEGRATION_TYPE.GOOGLE]: `/api/latest/google-disconnect`,
      }[integrationType],
    },
    invalidate: [
      { queryKey: ["coaches"], exact: false },
      { queryKey: ["coach-availability"], exact: false },
    ],
  });
};

const useCalendlyEventTypesQuery = ({ ...rest }) => {
  return useMyQuery({
    queryKey: ["coach-availability", "event-types"],
    fetchDef: {
      url: "/api/latest/coach-availability/event-types",
    },
    ...rest,
  });
};
